// Considered: ContextSwitcherToolbarMolecule (extend it with filter state) —
// rejected. The toolbar's job is context/view selection plus the definition
// save rail. Filters have four layers with different blast radii and a
// per-layer save menu; folding that in would give one component two
// unrelated save models. This bar composes WITH the toolbar (feeding its
// switch-guard dirty signal), not into it. See spec Reuse Audit
// (specs/context-filter-lifecycle.md).
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
// The two count badges are `StatusBadgeAtom`'s tone variant, not local spans:
// it already ships `warn`/`danger` tones with tokenized light AND dark
// treatments, which is exactly what a count badge here needs.
import StatusBadgeAtom from '../../atoms/StatusBadge/StatusBadgeAtom';
import FilterSaveTargetMolecule from '../FilterSaveTarget/FilterSaveTargetMolecule';
import type { FilterSaveTarget, FilterSaveTargetAvailability } from '../../../hooks/useLevelFilters';
import type { ComposedLevelFilters, DegradedFilterEntry } from 'types/contextView';
import './ContextFilterBarMolecule.scss';

/** One level's composed filters, plus the label the bar groups its (future, Phase 4) chips under. */
export interface ContextFilterBarLevelEntry {
  levelId: string;
  levelLabel: string;
  composed: ComposedLevelFilters;
}

export interface ContextFilterBarMoleculeProps {
  contextSystemName?: string | null;
  /** One entry per context level, in level order. */
  levels: ContextFilterBarLevelEntry[];
  /** Composed clauses whose target no longer resolves in their level's catalog — feeds the collapsed "K unavailable" count (EAINT-789 § Degraded-filter detection). */
  degraded: DegradedFilterEntry[];
  /** `levelId`s with a currently-buffered, uncommitted shared-filter edit (`useLevelFilters().pendingSharedFilterLevelIds`) — lets the bar attribute "unsaved" to the view layer only where a write is actually pending. */
  pendingSharedFilterLevelIds: string[];
  /**
   * Always all three targets, in blast-radius order, as `useLevelFilters`
   * derives them for the AUTHORING popover. The bar narrows them before
   * rendering — see `barSaveTargets`: only `view` has a deferred buffer a
   * later Save can commit.
   */
  saveTargets: FilterSaveTargetAvailability[];
  /** Only ever called with `'view'` — the bar disables the other two targets. */
  onSave: (target: FilterSaveTarget) => void;
  /** Clears session + personal clauses for every level (`useLevelFilters().reset`). Never touches view or context. */
  onReset: () => void;
  /**
   * Fires the Share action (EAINT-793) — encoding, budget check and
   * clipboard write all happen in the caller (`TreeMapOrganism`), which owns
   * `runtimeFilters` and the route; the bar only decides WHEN the action is
   * offered. Omitted entirely on a host that doesn't support sharing (e.g. a
   * bar rendered outside a shareable route); the button is absent, not
   * merely disabled, in that case.
   */
  onShare?: () => void;
  /** The expanded grouped-chip content (`FilterSummaryGroupMolecule`, EAINT-789 Phase 4). Rendered only while expanded; omitted here renders an empty expanded region. */
  children?: React.ReactNode;
  className?: string;
}

/**
 * The persistent filter bar (EAINT-789 § UI Components). A READER of
 * EAINT-781's per-level resolution — it renders `ComposedLevelFilters` and
 * routes actions back through `useLevelFilters`'s existing setters; nothing
 * here stores filter state. Renders `null` entirely when no layer contributes
 * a clause AND nothing is pending commit, per spec: an empty bar is chrome.
 */
const ContextFilterBarMolecule: React.FC<ContextFilterBarMoleculeProps> = ({
  contextSystemName,
  levels,
  degraded,
  pendingSharedFilterLevelIds,
  saveTargets,
  onSave,
  onReset,
  onShare,
  children,
  className = '',
}) => {
  const { t } = useTranslation();
  const logAnalyticsEvent = useAnalytics();
  const [expanded, setExpanded] = useState(false);

  const pendingLevelSet = useMemo(() => new Set(pendingSharedFilterLevelIds), [pendingSharedFilterLevelIds]);

  // `saveTargets` answers "where may a NEW clause be authored", which is the
  // popover's question. The bar's question is "what is still uncommitted",
  // and only the `view` buffer ever is: `session`/`user` are written through
  // at the moment of authoring (`useLevelFilters.setLevelFilter`), so a bar
  // Save has nothing to do for them. Rendering them selectable would fire
  // `context_filter_saved` for a no-op — the row is disabled WITH ITS REASON
  // instead, so the menu explains itself rather than silently doing nothing.
  // `link_exits_first` must NOT be flattened into `already_applied`: while a
  // shared link suppresses those layers they are applied to nothing, so
  // "already applied — this saves the moment you filter" would be a false
  // statement on the one surface guaranteed to be on screen during a link
  // (EAINT-793). The bar cannot offer the popover's exit-then-save either —
  // its Save only ever commits `view` — so the honest row here is disabled
  // with `link_active`.
  const barSaveTargets = useMemo<FilterSaveTargetAvailability[]>(
    () =>
      saveTargets.map((availability) => {
        if (availability.target === 'view') return availability;
        const reason = availability.reason === 'link_exits_first' ? 'link_active' : 'already_applied';
        return { target: availability.target, disabled: true, reason };
      }),
    [saveTargets]
  );

  // `unsaved` = clauses that require an explicit action to persist: `session`
  // clauses (never persisted, by design) plus `view`-layer clauses on a level
  // whose shared-filter buffer is currently pending. `user` clauses auto-save
  // without user action and `context` clauses aren't the caller's to save —
  // SC2: counted as active, never as unsaved.
  const { activeCount, unsavedCount, resettableCount } = useMemo(() => {
    let active = 0;
    let unsaved = 0;
    let resettable = 0;
    const pendingLevelsWithClauses = new Set<string>();
    levels.forEach(({ levelId, composed }) => {
      composed.provenance.forEach((entry) => {
        active += 1;
        if (entry.layer === 'session') {
          unsaved += 1;
          resettable += 1;
        } else if (entry.layer === 'view' && pendingLevelSet.has(levelId)) {
          unsaved += 1;
          pendingLevelsWithClauses.add(levelId);
        } else if (entry.layer === 'user') {
          resettable += 1;
        }
      });
    });
    // A pending buffer that REMOVED the level's last shared clause leaves no
    // provenance behind to count, so the loop above sees nothing — yet the
    // removal is still uncommitted and the bar is rendering solely to offer
    // the Save ▾ that commits it. Without this the bar reads "0 filters, 0 not
    // saved" while holding unsaved work, which is the one thing the count
    // exists to prevent.
    // Iterate the PENDING set, not `levels`: the bar's visibility test below
    // reads `pendingSharedFilterLevelIds` raw, so a pending id for a level that
    // is no longer in `contextDefinition.levels` (buffer a shared filter, then
    // edit the context to drop that level — the buffer only resets when the
    // context or view system name changes) would render the bar while counting
    // nothing. Counting off `levels` here made the two disagree.
    pendingLevelSet.forEach((levelId) => {
      if (!pendingLevelsWithClauses.has(levelId)) {
        unsaved += 1;
      }
    });
    return { activeCount: active, unsavedCount: unsaved, resettableCount: resettable };
  }, [levels, pendingLevelSet]);

  const unavailableCount = degraded.length;

  // What Share would actually encode: every provenance entry EXCEPT
  // `context` — the same "excluded by construction" rule
  // `composeLevelFilters` applies to `runtime` (specs/shareable-context-links.md
  // gap analysis G-3). A board filtered only by the context author has
  // nothing for a link to carry, so Share stays disabled rather than
  // offering to copy a link that reproduces nothing (Open Questions: "should
  // Share be offered when the only filters are context-authored? — no").
  const shareableCount = useMemo(() => {
    let count = 0;
    levels.forEach(({ composed }) => {
      composed.provenance.forEach((entry) => {
        if (entry.layer !== 'context') count += 1;
      });
    });
    return count;
  }, [levels]);

  const handleToggleExpanded = useCallback(() => {
    // Computed outside the updater: React may re-invoke a setState updater
    // (StrictMode, discarded concurrent renders), which would double-fire the event.
    const next = !expanded;
    if (next) {
      logAnalyticsEvent('context_filter_bar_expanded', undefined, {
        contextSystemName: contextSystemName ?? null,
        activeCount,
        unsavedCount,
        degradedCount: unavailableCount,
      });
    }
    setExpanded(next);
  }, [expanded, logAnalyticsEvent, contextSystemName, activeCount, unsavedCount, unavailableCount]);

  const handleReset = useCallback(() => {
    onReset();
    logAnalyticsEvent('context_filters_cleared', undefined, {
      contextSystemName: contextSystemName ?? null,
      scope: 'both',
      clearedCount: resettableCount,
    });
  }, [onReset, logAnalyticsEvent, contextSystemName, resettableCount]);

  // An empty bar is chrome — but only when there is also nothing left to
  // commit. Removing a level's last `view` clause buffers the removal and
  // empties `provenance`, so hiding on `activeCount` alone would take the
  // Save ▾ that commits it away with it.
  if (activeCount === 0 && pendingSharedFilterLevelIds.length === 0) return null;

  return (
    <div className={`context-filter-bar ${className}`.trim()}>
      <div className="context-filter-bar__summary">
        <ButtonAtom
          variant="text"
          size="small"
          icon={expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'}
          iconPos="left"
          aria-expanded={expanded}
          onClick={handleToggleExpanded}
          className="context-filter-bar__toggle"
          label={t('columnFilters.bar.filterCount', { count: activeCount })}
        />
        {unsavedCount > 0 && (
          <StatusBadgeAtom
            tone="warn"
            size="small"
            label={t('columnFilters.bar.notSavedCount', { count: unsavedCount })}
          />
        )}
        {unavailableCount > 0 && (
          <StatusBadgeAtom
            tone="danger"
            size="small"
            label={t('columnFilters.bar.unavailableCount', { count: unavailableCount })}
          />
        )}
        <div className="context-filter-bar__actions">
          <FilterSaveTargetMolecule targets={barSaveTargets} onSelect={onSave} contextSystemName={contextSystemName} />
          <ButtonAtom
            variant="secondary"
            size="small"
            disabled={resettableCount === 0}
            onClick={handleReset}
            label={t('columnFilters.bar.reset')}
          />
          {onShare && (
            <ButtonAtom
              variant="secondary"
              size="small"
              icon="pi pi-share-alt"
              iconPos="left"
              disabled={shareableCount === 0}
              onClick={onShare}
              label={t('columnFilters.bar.share')}
            />
          )}
        </div>
      </div>
      {expanded && <div className="context-filter-bar__expanded">{children}</div>}
    </div>
  );
};

export default ContextFilterBarMolecule;
